import { useTranslation, untranslated } from "@i18n";
import { useState, useRef, useMemo } from "react";
import { spacesInProjectTemplateUrl } from "@Services/dataManager";
import { useParams } from "react-router-dom";
import { convertDate } from "@Services/luxonHelper";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { spaceDataInProjectTemplate } from "@Actions/portalSetting";
import { showToastNotification } from "@Reducers/toastSlice";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import SlickGrid1, { typedColumns, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** Row type for space data in project template */
interface SpaceTemplateRow extends GridRowBase {
  /** Server returns Mongo ObjectId strings (narrows GridRowBase's RowId) — matches spaceId in the request schema */
  _id?: string;
  name?: string;
  description?: string;
  updatedDate?: number;
  updatedBy?: { name?: string };
  isDefault?: boolean;
}

function SpacesTab() {
  const { t } = useTranslation();
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const defaultSpaceData = {
    name: "",
    description: "",
  };
  const [SpaceDataToSave, setSpaceDataToSave] = useState(defaultSpaceData);

  const dispatch = useDispatch();
  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const dataGrid = useRef<SlickGrid1Ref<SpaceTemplateRow> | null>(null);
  // Route params enter from outside the type system; the backend route schema validates this id.
  const templateData = useParams<{ costDatabaseId: ObjectIdString }>();
  const projectTemplateId = templateData.costDatabaseId;

  const [getSpacesDataInProjectTemplateQuery] = useState({
    projectTemplateId,
    params: {
      projectTemplateId,
    },
  });

  const [toolBarBtnState, setToolBarBtnState] = useState({
    delete: false,
    add: true,
    edit: false,
    default: false,
  });

  const formatDate = (
    _row: number,
    _cell: number,
    _value: number | undefined,
    _columnDef: unknown,
    dataContext: SpaceTemplateRow,
  ) => {
    const dateFormat = convertDate(Number(dataContext.updatedDate), "dateForText");
    return dateFormat;
  };

  const canAdd = portalSettingInfo?.projectTemplates?.addProjectTemplates?.enable;
  const canEdit = portalSettingInfo?.projectTemplates?.editProjectTemplates?.enable;
  const canDelete = portalSettingInfo?.projectTemplates?.deleteProjectTemplates?.enable;

  const toolbarOptions = [
    {
      name: "Add Space",
      prefixIcon: "add",
      id: "custom_add",
      disabled: !toolBarBtnState.add || !canAdd,
    },
    {
      name: "Edit",
      prefixIcon: "editProfile",
      id: "custom_edit",
      disabled: !toolBarBtnState.edit || !canEdit,
    },
    {
      name: "Delete",
      prefixIcon: "delete",
      id: "custom_delete",
      disabled: !toolBarBtnState.delete || !canDelete,
    },
    {
      name: "Mark as Default",
      id: "custom_default",
      disabled: !toolBarBtnState.default || !canEdit,
    },
  ];

  const spaceName = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: SpaceTemplateRow,
  ) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <>
        <span>{dataContext.name}</span>
        <span className={dataContext.isDefault ? "bg-text_light rounded-lg px-1 py-0.5 ms-2 text-white text-xs" : ""}>
          {dataContext.isDefault ? "Default" : ""}
        </span>
      </>,
    );
    return container;
  };

  const selectionEvent = () => {
    if (!dataGrid?.current) {
      return;
    }
    const totalRowsSelected = dataGrid.current.dataView.getAllSelectedItems().length;

    setToolBarBtnState((prev) => ({
      ...prev,
      edit: totalRowsSelected == 1,
      default: totalRowsSelected == 1,
      delete: totalRowsSelected == 1,
    }));
  };

  const toolbarClickHandler = (id: string) => {
    switch (id) {
      case "custom_add":
        setOpenAddEditModal(true);
        break;
      case "custom_edit": {
        const grid = dataGrid.current;
        if (!grid) {
          break;
        }
        const selectedData = grid.dataView.getAllSelectedItems()[0];
        if (!selectedData) {
          break;
        }
        setSpaceDataToSave({
          name: selectedData.name ?? "",
          description: selectedData.description ?? "",
        });
        setOpenAddEditModal(true);
        setEditMode(true);
        break;
      }

      case "custom_default":
        handleSaveSpaceData({ EditMode: true, spaceData: { isDefault: true } });
        break;

      case "custom_delete":
        setOpenDeleteModal(true);
        break;
    }
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<SpaceTemplateRow>([
        {
          field: "name",
          id: "name",
          filterType: "Text",
          name: t("Name"),
          width: 150,
          formatter: spaceName,
          _excelValueGetter: (row) => {
            const name = row.name ?? "";
            return row.isDefault ? `${name} (Default)` : name;
          },
        },
        {
          field: "description",
          id: "description",
          filterType: "Text",
          name: t("Description"),
          width: 150,
        },
        {
          field: "updatedDate",
          id: "updatedDate",
          filterType: "Date",
          name: t("Modified On"),
          width: 150,
          formatter: formatDate,
          _excelValueGetter: (row) => (row.updatedDate ? convertDate(Number(row.updatedDate), "dateForText") : ""),
        },
        {
          field: "updatedBy",
          id: "updatedBy.name",
          filterType: "Text",
          name: t("Modified By"),
          width: 150,
          _valueGetter: (row) => row.updatedBy?.name,
          formatter: (_row, _cell, _value, _col, dataContext) => dataContext.updatedBy?.name,
        },
      ]),
    [],
  );
  const spacesInProjectTemplate = () => (
    <SlickGrid1
      ref={dataGrid}
      remoteDataSource={spacesInProjectTemplateUrl}
      query={getSpacesDataInProjectTemplateQuery}
      toolbar={toolbarOptions}
      toolbarClickHandler={toolbarClickHandler}
      onSelectedRowIdsChanged={selectionEvent}
      infiniteScroll
      enableSortingOption
      columnDefinitions={columnDefinitions}
      enableFilteringOption
      enableGroupingOption
      enableColumnChooser
      excelExport
    />
  );

  const addEditModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="30%"
      headerTitle={headerContent()}
      isVisible={openAddEditModal}
      onCancel={handleAddEditModalCLose}
      modelContent={modelContent()}
    />
  );

  const headerContent = () => (
    <div className=" w-full flex justify-between rounded-md">
      <Text>{editMode ? t("Edit Space") : t("Add Space")}</Text>
    </div>
  );

  const footerContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSaveSpaceData}
        disabled={!SpaceDataToSave.name}
      >
        <Text>{t("Save")}</Text>
      </ButtonComponent>

      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleAddEditModalCLose}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const modelContent = () => (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span>{t("Name")}</span>
        <span>
          <TextFieldComponent
            name="name"
            type="text"
            placeholder={t("Add Name")}
            showClearButton
            onChange={handleChange}
            value={SpaceDataToSave.name}
          />
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <Text>{t("Description (Optional)")}</Text>
        <span>
          <TextFieldComponent
            name="description"
            type="text"
            placeholder={t("Add Description")}
            showClearButton
            onChange={handleChange}
            value={SpaceDataToSave.description}
          />
        </span>
      </div>
    </div>
  );

  const handleChange = (args: React.ChangeEvent<HTMLInputElement>) => {
    if (args?.target) {
      const data = args.target.value;
      setSpaceDataToSave({
        ...SpaceDataToSave,
        [args.target.name]: data,
      });
    }
  };

  const handleSaveSpaceData = async ({
    EditMode = editMode,
    spaceData = SpaceDataToSave,
  }: { EditMode?: boolean; spaceData?: Partial<{ name: string; description: string; isDefault: boolean }> } = {}) => {
    const selectedData = EditMode ? dataGrid.current?.dataView.getAllSelectedItems()[0] : undefined;
    const data = {
      data: {
        projectTemplateId,
        data: {
          ...spaceData,
          spaceId: selectedData?._id,
        },
      },
      action: (EditMode ? "edit" : "insert") as "edit" | "insert",
    };
    const res = await spaceDataInProjectTemplate(data);
    if (res.data.status) {
      const content = EditMode
        ? t("The changes have been saved successfully.")
        : t("Space '{{name}}' has been added successfully.", { name: spaceData.name });
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setSpaceDataToSave(defaultSpaceData);
      handleAddEditModalCLose();
      if (dataGrid.current) {
        dataGrid.current.refresh?.();
      } else {
        // setIsDataEmpty(false);
      }
    }
  };

  const handleAddEditModalCLose = () => {
    setOpenAddEditModal(false);
    setOpenDeleteModal(false);
    setEditMode(false);
    setSpaceDataToSave(defaultSpaceData);
  };

  const deleteSpace = async () => {
    const grid = dataGrid.current;
    if (!grid) {
      return;
    }
    const rowData = grid.dataView.getAllSelectedItems()[0];
    if (!rowData) {
      return;
    }
    const data = {
      data: {
        projectTemplateId,
        data: {
          spaceId: rowData._id,
        },
      },
      action: "delete" as const,
    };
    const res = await spaceDataInProjectTemplate(data);
    setOpenDeleteModal(false);
    dataGrid.current?.refresh?.();
    if (res.data.status) {
      dispatch(
        showToastNotification({
          type: TOAST_TYPE_SUCCESS,
          content: t("Space {{name}} deleted successfully", { name: rowData.name }),
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          type: TOAST_TYPE_ERROR,
          content: res.data.message ? untranslated(res.data.message) : t("Something went wrong"),
        }),
      );
    }
  };

  const deleteModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex justify-normal gap-4">
          <ButtonComponent buttonType="gradientButton" onClick={deleteSpace}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType="borderButton" onClick={handleAddEditModalCLose}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      onCancel={handleAddEditModalCLose}
      width="30%"
      headerTitle={
        <div className="w-full flex justify-between rounded-md pb-3">
          <Text>{t("Delete Space")}</Text>
        </div>
      }
      isVisible={openDeleteModal}
      modelContent={
        <div className="flex flex-col justify-center pb-2 ">
          <Text className="mb-4 whitespace-normal">{t("Are you sure you want to delete the selected space?")}</Text>
        </div>
      }
    />
  );

  return (
    <div className="w-full flex flex-col h-full overflow-auto">
      {spacesInProjectTemplate()}
      {openAddEditModal ? addEditModal() : null}
      {openDeleteModal ? deleteModal() : null}
    </div>
  );
}
export default SpacesTab;
